import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import type { DocumentsSearchParams } from 'routes/documents/-searchParams'
import { getSearchStatePatchFromUrl } from 'routes/documents/-searchParams'

import {
  applySearchUrlPatchToTabs,
  createInitialTab,
} from './tabStateUrlHelpers'

const tabMatchesUrlDocument = (
  tab: DocumentTab,
  documentId: string
): boolean => {
  const item = tab.history[tab.currentIndex]
  return item.type === 'document' && item.documentId === documentId
}

/**
 * Aligns open tabs with document/search intent already present in the URL.
 * Returns null when the URL has no document/search intent to apply, or when
 * the active tab already matches the URL document (so duplicate-doc tabs keep
 * the caller-selected tab id instead of jumping to the first match).
 */
export function reconcileTabsWithDocumentsUrl(
  openTabs: DocumentTab[],
  activeTabId: string,
  search: DocumentsSearchParams
): { openTabs: DocumentTab[]; activeTabId: string } | null {
  const urlDocumentId = search.documentID
  const hasUrlDocument = !!urlDocumentId

  if (hasUrlDocument && urlDocumentId) {
    const activeTab = openTabs.find((tab) => tab.id === activeTabId)
    if (activeTab && tabMatchesUrlDocument(activeTab, urlDocumentId)) {
      return null
    }

    const existingTab = openTabs.find((tab) =>
      tabMatchesUrlDocument(tab, urlDocumentId)
    )
    if (existingTab) {
      return { openTabs, activeTabId: existingTab.id }
    }
    const newTab = createInitialTab(
      urlDocumentId,
      search.pageNumber,
      search.selectedBoxes,
      search.selectedRegions
    )
    return {
      openTabs: [...openTabs, newTab],
      activeTabId: newTab.id,
    }
  }

  const urlPatch = getSearchStatePatchFromUrl(search)
  if (!urlPatch || Object.keys(urlPatch).length === 0) return null

  return applySearchUrlPatchToTabs(openTabs, activeTabId, urlPatch)
}
